import { useRef, useState, useEffect, type CSSProperties } from 'react';
import { useAudioSettings } from '@/contexts/AudioSettingsContext';
import { getRevealVideoSrc } from '@/components/reveal/revealAnimationAssets';

const FADE_START_SEC = 6;
const FADE_DURATION_SEC = 2;
const POST_END_DELAY_MS = 200;

/**
 * Mobile/in-app only (below the `md` breakpoint): nudge the video composition
 * down so the pack in the final frames lines up with the card position on the
 * selection screen, which sits lower inside the embedded app viewport.
 *
 * The scale oversizes the video so the downward shift stays cropped by the
 * overflow-hidden wrapper instead of exposing background at the top — keep
 * MOBILE_VIDEO_SHIFT_Y_PX ≤ (MOBILE_VIDEO_SCALE − 1) / 2 × viewport height.
 */
const MOBILE_VIDEO_SHIFT_Y_PX = 30;
const MOBILE_VIDEO_SCALE = 1.05;

type RevealVideoProps = {
  onComplete: () => void;
  /** Contest sport — NFL plays the gridiron reveal clip, NBA the Full Court Draw one. */
  sport?: string | null;
};

function getOverlayOpacity(currentTimeSec: number): number {
  if (currentTimeSec < FADE_START_SEC) return 1;
  const fadeProgress = (currentTimeSec - FADE_START_SEC) / FADE_DURATION_SEC;
  return Math.max(0, 1 - fadeProgress);
}

export default function RevealVideo({ onComplete, sport = null }: RevealVideoProps) {
  const { isMuted } = useAudioSettings();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [hasEnded, setHasEnded] = useState(false);
  const videoSrc = getRevealVideoSrc(sport);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setIsVideoReady(false);
    setCurrentTimeSec(0);
    setHasEnded(false);

    const handleCanPlay = () => {
      setIsVideoReady(true);
    };

    video.addEventListener('canplaythrough', handleCanPlay, { once: true });
    video.src = videoSrc;
    video.load();

    return () => {
      video.removeEventListener('canplaythrough', handleCanPlay);
    };
  }, [videoSrc]);

  useEffect(() => {
    if (!isVideoReady) return;

    const video = videoRef.current;
    if (!video) return;

    let rafId: number;
    const tick = () => {
      setCurrentTimeSec(video.currentTime);
      if (!video.ended) {
        rafId = requestAnimationFrame(tick);
      }
    };

    const handleEnded = () => {
      cancelAnimationFrame(rafId);
      setHasEnded(true);
    };

    rafId = requestAnimationFrame(tick);
    video.addEventListener('ended', handleEnded);
    void video.play().catch(() => {
      setTimeout(() => setHasEnded(true), 100);
    });

    return () => {
      cancelAnimationFrame(rafId);
      video.removeEventListener('ended', handleEnded);
    };
  }, [isVideoReady]);

  useEffect(() => {
    if (!hasEnded) return;
    const timer = window.setTimeout(() => {
      onComplete();
    }, POST_END_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [hasEnded, onComplete]);

  const overlayOpacity = hasEnded ? 0 : getOverlayOpacity(currentTimeSec);

  return (
    <div
      className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center overflow-hidden bg-[#121212]"
      aria-hidden
      style={{ opacity: overlayOpacity }}
    >
      {!isVideoReady ? (
        <div className="absolute inset-0 flex items-center justify-center" aria-label="Loading">
          <div
            className="h-12 w-12 animate-spin rounded-full border-2 border-[#2c2c2c] border-t-[#53d337]"
            role="status"
          />
        </div>
      ) : null}
      <video
        ref={videoRef}
        className="h-full w-full object-contain max-md:[transform:translateY(var(--reveal-video-shift-y))_scale(var(--reveal-video-scale))]"
        style={
          {
            '--reveal-video-shift-y': `${MOBILE_VIDEO_SHIFT_Y_PX}px`,
            '--reveal-video-scale': `${MOBILE_VIDEO_SCALE}`,
          } as CSSProperties
        }
        playsInline
        muted={isMuted}
        preload="auto"
      />
    </div>
  );
}
